import { COOKIE_NAMES, type EventType } from "@ets/types";
import { resolveAnonymousId } from "./anonymous-id.js";
import { EventBus } from "./bus.js";
import type { ETSConfig, ETSResolvedConfig } from "./config.js";
import { resolveConfig } from "./config.js";
import { collectPageContext } from "./context.js";
import { deleteCookie, getCookie } from "./cookie.js";
import { createOfflineQueue } from "./offline-queue.js";
import { resolvePageviewId } from "./pageview-id.js";
import type { DebugStats, ETSPlugin, ETSPluginCtx } from "./plugin-api.js";
import type { QueuedEvent } from "./queue.js";
import { BatchQueue } from "./queue.js";
import { resolveSessionId } from "./session-id.js";
import type { TransportPayload } from "./transport.js";
import { createTransport, sendBeacon } from "./transport.js";
import { resolveVisitorId } from "./visitor-id.js";

/** Full event shape built by ETS before enqueuing into BatchQueue. */
interface TrackedEvent extends QueuedEvent {
  envelope_version: "1";
  message_id: string;
  sent_at: string;
  type: EventType;
  write_key: string;
  visitor_id: string;
  anonymous_id: string;
  session_id: string;
  server_session_id?: string;
  pageview_id: string;
  user_id?: string;
  context: Record<string, unknown>;
  properties: Record<string, unknown>;
}

const SDK_NAME = "ets-web";
const SDK_VERSION = "0.0.1";

export class ETS {
  private readonly config: ETSResolvedConfig;
  private readonly batchQueue: BatchQueue;
  private readonly transport: ReturnType<typeof createTransport>;
  private visitorId: string;
  private anonymousId: string;
  private userId: string | undefined;
  private initialized = false;
  private readyCallbacks: Array<() => void> = [];

  private readonly plugins = new Map<string, ETSPlugin>();
  private readonly bus = new EventBus();
  private readonly _metrics = {
    tracked: 0,
    queuedOffline: 0,
    batchesSent: 0,
    batchesFailed: 0,
    lastError: undefined as string | undefined,
    pluginErrors: [] as Array<{ plugin: string; message: string }>,
  };

  static instance: ETS | null = null;

  constructor(config: ETSConfig) {
    this.config = resolveConfig(config);
    this.visitorId = resolveVisitorId();
    this.anonymousId = resolveAnonymousId();

    this.transport = createTransport({
      endpoint: this.config.endpoint,
      writeKey: this.config.writeKey,
      maxRetries: this.config.maxRetries,
      debug: this.config.debug,
      shadow: this.config.shadow,
      legacyMode: this.config.legacyMode,
    });

    const offlineQueue = createOfflineQueue();

    this.batchQueue = new BatchQueue({
      flushAt: this.config.flushAt,
      flushInterval: this.config.flushInterval,
      offlineQueue,
      onFlush: async (batch) => {
        const payload: TransportPayload = {
          schemaVersion: "ingest_http_v2",
          sentAt: new Date().toISOString(),
          sdk: { name: SDK_NAME, version: SDK_VERSION },
          batch,
        };
        try {
          await this.transport.send(payload);
          this._metrics.batchesSent++;
        } catch (err) {
          this._metrics.batchesFailed++;
          this._metrics.lastError = err instanceof Error ? err.message : String(err);
          this.bus.emit("sdk:transport-failed", { error: err, batch });
          // Rethrow so BatchQueue persists the batch to the offline queue
          // (when offline) and upstream callers can observe the failure.
          throw err;
        }
      },
      onBeaconFlush: (batch) => {
        if (this.config.shadow) {
          if (this.config.debug) {
            // biome-ignore lint/suspicious/noConsole: shadow dry-run observability
            console.info("[ets] shadow: dropped beacon batch of", batch.length, "events");
          }
          return true;
        }
        const payload: TransportPayload = {
          schemaVersion: "ingest_http_v2",
          sentAt: new Date().toISOString(),
          sdk: { name: SDK_NAME, version: SDK_VERSION },
          batch,
        };
        return sendBeacon(this.config.endpoint, payload);
      },
    });

    this.batchQueue.start();

    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        this.bus.emit("sdk:online");
        void this.batchQueue.flush().catch(() => undefined);
      });
      window.addEventListener("offline", () => {
        this.bus.emit("sdk:offline");
      });
    }

    this.initialized = true;

    for (const cb of this.readyCallbacks) {
      cb();
    }
    this.readyCallbacks = [];
  }

  use(plugin: ETSPlugin): void {
    if (this.plugins.has(plugin.name)) return;
    const ctx = this.makeCtx(plugin.name);
    try {
      const result = plugin.init(ctx);
      if (result && typeof (result as Promise<void>).catch === "function") {
        (result as Promise<void>).catch((err) => this.handlePluginError(err, plugin.name));
      }
      this.plugins.set(plugin.name, plugin);
    } catch (err) {
      this.handlePluginError(err, plugin.name);
    }
  }

  private handlePluginError(err: unknown, pluginName: string): void {
    const message = err instanceof Error ? err.message : String(err);
    this._metrics.pluginErrors.push({ plugin: pluginName, message });
    if (this.config.debug) console.warn(`[ets] plugin ${pluginName} error: ${message}`);
  }

  private makeCtx(pluginName: string): ETSPluginCtx {
    return {
      track: (event, props) => {
        this.track(event as never, props as Record<string, unknown>);
      },
      identify: (userId, traits) => {
        this.identify(userId, traits as Record<string, unknown>);
      },
      config: this.config,
      bus: this.bus,
      debug: (msg, ...args) => {
        if (this.config.debug) console.debug(`[ets:${pluginName}] ${msg}`, ...args);
      },
    };
  }

  getDebugStats(): DebugStats {
    return {
      pluginsLoaded: [...this.plugins.keys()],
      eventsTracked: this._metrics.tracked,
      eventsQueuedOffline: this.batchQueue.queuedOfflineCount,
      batchesSent: this._metrics.batchesSent,
      batchesFailed: this._metrics.batchesFailed,
      lastError: this._metrics.lastError,
      pluginErrors: [...this._metrics.pluginErrors],
    };
  }

  track(event: string, properties?: Record<string, unknown>): void {
    this._metrics.tracked++;
    this.enqueue("track", { event_name: event, ...properties });
  }

  page(properties?: Record<string, unknown>): void {
    this.enqueue("page", properties ?? {});
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    this.userId = userId;
    this.enqueue("identify", traits ?? {});
  }

  group(groupId: string, traits?: Record<string, unknown>): void {
    this.enqueue("group", { group_id: groupId, ...traits });
  }

  alias(userId: string, previousId?: string): void {
    this.enqueue("alias", {
      previous_id: previousId ?? this.anonymousId,
    });
    this.userId = userId;
  }

  setSessionProperties(props: Record<string, unknown>): void {
    this.enqueueSessionPropertiesEvent(props);
  }

  clearSessionProperty(key: string): void {
    this.enqueueSessionPropertiesEvent({ [key]: null });
  }

  clearSessionProperties(): void {
    this.enqueueSessionPropertiesEvent({ _clear: true });
  }

  ready(callback: () => void): void {
    if (this.initialized) {
      callback();
    } else {
      this.readyCallbacks.push(callback);
    }
  }

  async flush(): Promise<void> {
    await this.batchQueue.flush();
  }

  destroy(): void {
    this.batchQueue.stop();
  }

  reset(): void {
    // Clear identity cookies
    deleteCookie(COOKIE_NAMES.VISITOR_ID);
    deleteCookie(COOKIE_NAMES.ANONYMOUS_ID);
    deleteCookie(COOKIE_NAMES.CLIENT_SESSION_ID);
    deleteCookie(COOKIE_NAMES.PAGEVIEW_ID);

    // Clear localStorage backups (prefix "ets_" + cookie name)
    try {
      localStorage.removeItem(`ets_${COOKIE_NAMES.VISITOR_ID}`);
      localStorage.removeItem(`ets_${COOKIE_NAMES.ANONYMOUS_ID}`);
      localStorage.removeItem(`ets_${COOKIE_NAMES.CLIENT_SESSION_ID}`);
    } catch {}

    // Generate fresh IDs
    this.visitorId = resolveVisitorId();
    this.anonymousId = resolveAnonymousId();
    this.userId = undefined;
  }

  /** @internal — exposed for testing */
  get _queue(): readonly QueuedEvent[] {
    return this.batchQueue._items;
  }

  /** @internal — exposed for testing */
  get _anonymousId(): string {
    return this.anonymousId;
  }

  private enqueueSessionPropertiesEvent(properties: Record<string, unknown>): void {
    this.enqueue("session_properties_set", properties);
  }

  private enqueue(type: EventType, properties: Record<string, unknown>): void {
    const session = resolveSessionId();
    const pageviewId = resolvePageviewId();
    const serverSessionId = getCookie(COOKIE_NAMES.SERVER_SESSION_ID);

    const event: TrackedEvent = {
      envelope_version: "1",
      message_id: crypto.randomUUID(),
      sent_at: new Date().toISOString(),
      type,
      write_key: this.config.writeKey,
      visitor_id: this.visitorId,
      anonymous_id: this.anonymousId,
      session_id: session.sessionId,
      ...(serverSessionId ? { server_session_id: serverSessionId } : {}),
      pageview_id: pageviewId,
      ...(this.userId ? { user_id: this.userId } : {}),
      context: collectPageContext(),
      properties,
    };

    this.batchQueue.enqueue(event);
  }
}
